import React, { useState, useEffect } from 'react';
import ReactMarkdown from "react-markdown";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faEye, faPlus } from '@fortawesome/free-solid-svg-icons';

const LessonContent = ({ eventId, levelId }) => {
  // For now, using localStorage to persist lessons since DB schema wasn't modified
  const STORAGE_KEY = `lessons_${eventId}_${levelId}`;

  const [lessons, setLessons] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLesson, setNewLesson] = useState({
    title: '',
    description: '',
    contentType: 'text', // text, html, markdown, video, pdf, iframe
    content: '',
    isOptional: false
  });
  const [previewContent, setPreviewContent] = useState('');
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load lessons from localStorage
  useEffect(() => {
    loadLessons();
  }, [eventId, levelId]);

  const loadLessons = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setLessons(JSON.parse(stored));
      } else {
        setLessons([]);
      }
    } catch (error) {
      console.error('Failed to load lessons:', error);
      setLessons([]);
    } finally {
      setLoading(false);
    }
  };

  const saveLessons = (updatedLessons) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedLessons));
      setLessons(updatedLessons);
    } catch (error) {
      console.error('Failed to save lessons:', error);
      alert('Failed to save lesson');
    }
  };

  const handleAddLesson = () => {
    if (!newLesson.title || !newLesson.content) {
      alert('Please fill in title and content');
      return;
    }

    const lesson = {
      id: Date.now(),
      ...newLesson
    };

    saveLessons([...lessons, lesson]);
    setNewLesson({ title: '', description: '', contentType: 'text', content: '', isOptional: false });
    setIsModalOpen(false);
  };

  const handlePreview = (lesson) => {
    setSelectedLesson(lesson);
    setPreviewContent(lesson.content);
    setIsPreviewModalOpen(true);
  };

  const handleSelectLesson = (lesson) => {
    setSelectedLesson(lesson);
  };

  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    setNewLesson(lesson);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this lesson?')) {
      saveLessons(lessons.filter(lesson => lesson.id !== id));
      if (selectedLesson?.id === id) {
        setSelectedLesson(null);
      }
    }
  };

  const handleUpdateLesson = () => {
    if (!newLesson.title || !newLesson.content) {
      alert('Please fill in title and content');
      return;
    }

    const updated = lessons.map(lesson =>
      lesson.id === editingLesson.id 
        ? { ...newLesson, id: lesson.id }
        : lesson
    );
    saveLessons(updated);
    setEditingLesson(null);
    setNewLesson({ title: '', description: '', contentType: 'text', content: '', isOptional: false });
    setIsModalOpen(false);
  };

  const handleSaveLesson = () => {
    if (editingLesson) {
      handleUpdateLesson();
    } else {
      handleAddLesson();
    }
  };

  const renderContentPreview = () => {
    switch (selectedLesson?.contentType) {
      case 'markdown':
        return <ReactMarkdown>{selectedLesson.content}</ReactMarkdown>;
      case 'html':
        return <div dangerouslySetInnerHTML={{ __html: selectedLesson.content }} />;
      case 'video':
        return <video controls className="w-full" src={selectedLesson.content} />;
      case 'iframe':
        return <iframe title="lesson-content" className="w-full h-96" src={selectedLesson.content} />;
      case 'pdf':
        return <iframe title="lesson-content" className="w-full h-96" src={selectedLesson.content} />;
      default:
        return <p className="whitespace-pre-wrap">{selectedLesson?.content}</p>;
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading lessons...</div>;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4 bg-white p-4 rounded-lg">
        <h1 className="text-xl font-bold">Lessons ({lessons.length})</h1>
        <button
          className="bg-gray-800 hover:bg-green-600 text-white p-2 rounded flex items-center gap-2"
          onClick={() => {
            setEditingLesson(null);
            setNewLesson({ title: '', description: '', contentType: 'text', content: '', isOptional: false });
            setIsModalOpen(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Lesson
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lessons List */}
        <div>
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 border-b font-semibold">Lesson List</div>
            <div className="max-h-96 overflow-y-auto">
              {lessons.length === 0 ? (
                <div className="p-4 text-gray-500 text-center">No lessons yet</div>
              ) : (
                lessons.map(lesson => (
                  <div
                    key={lesson.id}
                    className={`p-4 border-b cursor-pointer hover:bg-blue-50 transition ${
                      selectedLesson?.id === lesson.id ? 'bg-blue-100 border-l-4 border-l-blue-500' : ''
                    }`}
                    onClick={() => handleSelectLesson(lesson)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-grow">
                        <h3 className="font-semibold text-sm">{lesson.title}</h3>
                        {lesson.description && (
                          <p className="text-xs text-gray-600 mt-1 truncate">{lesson.description}</p>
                        )}
                        <span className={`inline-block mt-2 text-xs px-2 py-1 rounded ${
                          lesson.contentType === 'video' ? 'bg-red-100 text-red-700' :
                          lesson.contentType === 'markdown' ? 'bg-purple-100 text-purple-700' :
                          lesson.contentType === 'html' ? 'bg-orange-100 text-orange-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {lesson.contentType}
                        </span>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(lesson);
                          }}
                          className="text-blue-500 hover:text-blue-700 p-1"
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faEdit} size="sm" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(lesson.id);
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Lesson Preview */}
        <div>
          {selectedLesson ? (
            <div className="bg-white rounded-lg shadow-md h-96 overflow-hidden flex flex-col">
              <div className="p-4 border-b font-semibold flex justify-between items-center">
                <span>{selectedLesson.title}</span>
                <button
                  onClick={() => handlePreview(selectedLesson)}
                  className="text-gray-600 hover:text-gray-800"
                  title="Preview in full screen"
                >
                  <FontAwesomeIcon icon={faEye} />
                </button>
              </div>
              <div className="flex-grow overflow-y-auto p-4">
                {renderContentPreview()}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md h-96 flex items-center justify-center text-gray-500">
              <p>Select a lesson to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Lesson Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-96 overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editingLesson ? 'Edit Lesson' : 'Add Lesson'}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold">Lesson Title *</label>
                <input
                  type="text"
                  value={newLesson.title}
                  onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  placeholder="Enter lesson title"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Description</label>
                <textarea
                  value={newLesson.description}
                  onChange={(e) => setNewLesson({ ...newLesson, description: e.target.value })}
                  className="w-full p-2 border rounded-md h-16"
                  placeholder="Enter lesson description"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Content Type</label>
                <select
                  value={newLesson.contentType}
                  onChange={(e) => setNewLesson({ ...newLesson, contentType: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="text">Plain Text</option>
                  <option value="html">HTML</option>
                  <option value="markdown">Markdown</option>
                  <option value="video">Video URL</option>
                  <option value="pdf">PDF URL</option>
                  <option value="iframe">iFrame URL</option>
                </select>
              </div>

              <div>
                <label className="block mb-2 font-semibold">Content *</label>
                <textarea
                  value={newLesson.content}
                  onChange={(e) => setNewLesson({ ...newLesson, content: e.target.value })}
                  className="w-full p-2 border rounded-md h-24"
                  placeholder={`Enter lesson content (for ${newLesson.contentType})`}
                />
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newLesson.isOptional}
                  onChange={(e) => setNewLesson({ ...newLesson, isOptional: e.target.checked })}
                  className="mr-2"
                />
                <span>Optional Lesson</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingLesson(null);
                  setNewLesson({ title: '', description: '', contentType: 'text', content: '', isOptional: false });
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLesson}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                {editingLesson ? 'Update' : 'Add'} Lesson
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {isPreviewModalOpen && selectedLesson && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{selectedLesson.title}</h2>
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="text-gray-600 hover:text-gray-800 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="mb-4">
              {renderContentPreview()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonContent;
